import { useMutation, useQueryClient } from 'react-query';
import { BuildingProps } from 'services/hooks/building-service';

const useSaveBuilding = ({
  createQuery,
  updateQuery,
  queryName,
  onSuccess
}: {
  createQuery: (newBuilding: BuildingProps) => Promise<BuildingProps>;
  updateQuery: (uuid: string | undefined, updatedBuilding: BuildingProps) => Promise<BuildingProps>;
  queryName: string;
  onSuccess?: (response: BuildingProps) => void;
}) => {
  /* The query client is responsible for managing and caching the data fetched from API endpoints. It provides methods to interact with the 
  cache, such as retrieving data, updating data, and invalidating queries. */
  const queryClient = useQueryClient();

  /* The `useMutation` hook is used to create a mutation function that can be used to perform create or update operations on a Building. */
  const saveBuildingMutation = useMutation(
    async ({ uuid, ...restBuildingProperties }: BuildingProps): Promise<BuildingProps> => {
      if (uuid) {
        /* The below code is calling the `updateQuery` function with the `uuid` and `restBuildingProperties` as arguments. This function is 
        responsible for updating an existing Building with the provided properties. The `await` keyword is used to wait for the `updateQuery` 
        function to complete and return the data. The returned data is then assigned to the `data` variable and returned from the mutation 
        function.*/
        const data = await updateQuery(uuid, restBuildingProperties);
        return data;
      } else {
        /* The below code is calling the `createQuery` function with the `restBuildingProperties` as an argument. This function is responsible 
        for creating a new Building with the provided properties. The `await` keyword is used to wait for the `createQuery` function to complete 
        and return the data. The returned data is then assigned to the `data` variable and returned from the mutation function. */
        const data = await createQuery(restBuildingProperties);
        return data;
      }
    },
    {
      onSuccess: async (response) => {
        /* Both the below codes are used to remove specific queries from the query client's cache. */
        await queryClient.removeQueries('building');
        await queryClient.removeQueries(queryName);

        /* The code is checking if the `onSuccess` callback function is provided as a parameter to the `useSaveBuilding` hook. If it is provided, 
        the `onSuccess` function is called with the `response` as an argument. This allows the consumer of the hook to define custom logic to 
        be executed after the mutation is successful. */
        if (onSuccess) {
          onSuccess(response);
        }
      }
    }
  );
  return saveBuildingMutation;
};

export default useSaveBuilding;
